import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ScrollText } from "lucide-react";
import { z } from "zod";
import { PageHeader } from "~/components/shell/app-shell";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { DatePicker } from "~/components/ui/date-picker";
import { EmptyState, Skeleton } from "~/components/ui/feedback";
import { Field } from "~/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { auditActionGroups, describeAudit } from "~/features/audit/labels";
import { api, unwrap } from "~/lib/api";
import { errorMessage, meQuery } from "~/lib/auth";
import { appDayBounds, formatInstant } from "~/lib/format";

const searchSchema = z.object({
  group: z.string().catch("all").default("all"),
  from: z.string().date().optional().catch(undefined),
  to: z.string().date().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/settings/audit-log")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (me?.user.role !== "super_admin") throw redirect({ to: "/dashboard" });
  },
  component: AuditLogPage,
});

/** Short, safe summary of the interesting metadata fields. */
function summarise(metadata: Record<string, unknown>): string {
  const parts: string[] = [];
  if (typeof metadata.email === "string") parts.push(metadata.email);
  if (metadata.from && metadata.to) parts.push(`${metadata.from} → ${metadata.to}`);
  if (typeof metadata.method === "string") parts.push(`method: ${metadata.method}`);
  if (typeof metadata.reason === "string") parts.push(metadata.reason);
  if (typeof metadata.count === "number") parts.push(`${metadata.count} affected`);
  return parts.join(" · ");
}

function AuditLogPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const toDate = (value?: string) => (value ? new Date(`${value}T00:00:00`) : null);

  const entries = useInfiniteQuery({
    queryKey: ["audit-logs", search] as const,
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.api["audit-logs"].$get({
          query: {
            ...(search.group !== "all" ? { action: search.group } : {}),
            ...(search.from ? { from: appDayBounds(search.from).start.toISOString() } : {}),
            ...(search.to ? { to: appDayBounds(search.to).end.toISOString() } : {}),
            ...(pageParam ? { cursor: String(pageParam) } : {}),
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows = entries.data?.pages.flatMap((page) => page.entries) ?? [];
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) });

  return (
    <>
      <PageHeader
        title="Audit log"
        description="A permanent record of sign-ins, security changes and sensitive actions. Entries can't be edited or deleted."
      />

      <Card padding="sm" className="mb-4 grid gap-3 sm:grid-cols-3">
        <Field label="Activity">
          <Select value={search.group} onValueChange={(group) => setSearch({ group })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {auditActionGroups.map((group) => (
                <SelectItem key={group.value} value={group.value}>
                  {group.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="From">
          <DatePicker
            value={toDate(search.from)}
            onChange={(date) => setSearch({ from: date ? format(date, "yyyy-MM-dd") : undefined })}
            placeholder="Any time"
          />
        </Field>
        <Field label="To">
          <DatePicker
            value={toDate(search.to)}
            onChange={(date) => setSearch({ to: date ? format(date, "yyyy-MM-dd") : undefined })}
            placeholder="Now"
          />
        </Field>
      </Card>

      <Card padding="none" className="overflow-hidden">
        {entries.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : entries.isError ? (
          <EmptyState
            icon={<ScrollText />}
            title="Couldn't load the audit log"
            description={errorMessage(entries.error)}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ScrollText />}
            title="No matching activity"
            description="Try a wider date range."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>When</TableHead>
                <TableHead>Who</TableHead>
                <TableHead>What</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>IP address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="whitespace-nowrap text-fg-secondary" data-tabular>
                    {formatInstant(entry.occurredAt, "d MMM yyyy, HH:mm:ss")}
                  </TableCell>
                  <TableCell className="max-w-56 truncate">
                    {entry.actorEmail ??
                      (entry.actorType === "system" ? "Purlink" : "Unknown visitor")}
                  </TableCell>
                  <TableCell className="font-medium">{describeAudit(entry.action)}</TableCell>
                  <TableCell className="max-w-72 truncate text-fg-secondary">
                    {summarise(entry.metadata)}
                  </TableCell>
                  <TableCell className="text-fg-muted" data-tabular>
                    {entry.ip ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {entries.hasNextPage && (
          <div className="flex justify-center border-t border-border p-3">
            <Button
              variant="ghost"
              loading={entries.isFetchingNextPage}
              onClick={() => entries.fetchNextPage()}
            >
              Load more
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
